import { APP } from '@/app.config'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { AppIcon, Button, ToastProvider, useToast } from '@/components/ui'
import { ReauthSheet } from '@/features/auth/ReauthSheet'
import { useSyncTriggers } from '@/features/common/useSyncTriggers'
import { ServiceWorkerUpdater } from '@/pwa/ServiceWorkerUpdater'
import { router } from '@/routes'
import { useAppStore } from '@/store/appStore'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { RouterProvider } from 'react-router/dom'

/**
 * Owns the startup states. The router mounts only once the offline copy is in
 * memory, so no screen has to cope with a half-loaded store.
 */
export default function App() {
  const status = useAppStore((s) => s.status)
  const hydrate = useAppStore((s) => s.hydrate)

  useEffect(() => {
    void hydrate()
  }, [hydrate])

  if (status === 'error') return <StartupError onRetry={() => void hydrate()} />
  if (status === 'loading') {
    return (
      <div className="splash">
        <div className="t-display">{APP.name}</div>
        <div className="splash__rule" />
      </div>
    )
  }

  return (
    <ToastProvider>
      <ErrorBoundary>
        <RouterProvider router={router} />
      </ErrorBoundary>
      <SignedInServices />
      <ServiceWorkerUpdater />
    </ToastProvider>
  )
}

function SignedInServices() {
  const signedIn = useAppStore((s) => s.status === 'signedIn')
  useSyncTriggers(signedIn)
  useWriteErrorToast()
  return signedIn ? <ReauthSheet /> : null
}

/** A write that never reached the phone's storage is announced, never swallowed. */
function useWriteErrorToast(): void {
  const { t } = useTranslation()
  const toast = useToast()
  const lastWriteError = useAppStore((s) => s.lastWriteError)
  const dismiss = useAppStore((s) => s.dismissWriteError)
  useEffect(() => {
    if (!lastWriteError) return
    toast(t('storageWriteFailed'), { kind: 'error' })
    dismiss()
  }, [lastWriteError, dismiss, toast, t])
}

/** The offline copy will not open (private mode, storage blocked, disk full). */
function StartupError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="splash" role="alert">
      <div className="crash__mark">
        <AppIcon name="alert" size={24} />
      </div>
      <div className="t-title">{t('startupErrorTitle')}</div>
      <div className="t-body t-muted">{t('startupErrorBody')}</div>
      <div className="splash__action">
        <Button onClick={onRetry}>{t('startupErrorRetry')}</Button>
      </div>
    </div>
  )
}
